import React, { useState, useEffect } from "react";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import { MInputField } from "@/components/ui/atoms/MInputField";
import { MButton } from "@/components/ui/atoms/MButtons";
import styles from "./MOtp.module.css";
import { IconArrowNarrowRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

// Types
export interface MOtpProps {
  open: boolean;
  onClose: () => void;
  // onDone returns a promise: resolves true (verified) or throws error message
  onDone: (otp: string) => Promise<true | string>;
  onResend?: () => void;
  onEdit?: () => void;
  title?: string;
  sendTo?: string;
  loading?: boolean;
  resendTimer?: number;
  height?: string;
}

export const MOtp: React.FC<MOtpProps> = ({
  open,
  onClose,
  onDone,
  onResend,
  onEdit,
  title = "Enter 6-digit Code",
  sendTo,
  loading = false,
  resendTimer = 30,
  height = "50vh",
}) => {
  const { t } = useTranslation();
  const [otpValue, setOtpValue] = useState("");
  const [internalTimer, setInternalTimer] = useState(0);
  const [state, setState] = useState<"Default" | "Error" | "Success">(
    "Default"
  );
  const [stateMessage, setStateMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset OTP value and start timer when drawer opens
  useEffect(() => {
    if (open) {
      setOtpValue("");
      setInternalTimer(resendTimer);
      setState("Default");
      setStateMessage("");
    } else {
      setInternalTimer(0);
    }
  }, [open, resendTimer]); // Include resendTimer in dependencies

  // Timer countdown effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (internalTimer > 0) {
      interval = setInterval(() => {
        setInternalTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [internalTimer]);

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, ""); // Only allow digits
    if (val.length <= 6) {
      setOtpValue(val);
    }
  };

  const handleSubmit = async () => {
    if (otpValue.length === 6 && !submitting) {
      setSubmitting(true);
      setState("Default");
      setStateMessage("");
      try {
        const result = await onDone(otpValue);
        if (result === true) {
          setState("Success");
          setStateMessage("Verification Successful");
          setTimeout(() => {
            setState("Default");
            setStateMessage("");
            setOtpValue("");
            setSubmitting(false);
            onClose();
          }, 1000);
        } else if (typeof result === "string") {
          setState("Error");
          setStateMessage(result);
          setSubmitting(false);
        }
      } catch (err: any) {
        setState("Error");
        setStateMessage(err?.message || "Verification failed");
        setSubmitting(false);
      }
    }
  };

  const handleResend = () => {
    if (onResend && internalTimer === 0 && !loading) {
      onResend();
      setInternalTimer(resendTimer); // Reset timer after resend
    }
  };

  return (
    <FooterDrawer
      open={open}
      onClose={onClose}
      title={title}
      showClose={true}
      onDone={undefined}
      height={height}
      className="mOtpDrawer"
    >
      <div className={styles.otpWrap}>
        {/* Send To Info */}
        {sendTo && (
          <div className="auth-otp-info">
            <span>
              {t("otp.codeSent")} {sendTo}
            </span>
            <MButton
              variant="link"
              size="md"
              className="auth-otp-action-btn auth-otp-edit ml-auto"
              disabled={loading}
              onClick={onEdit || onClose}
            >
              {t("common.edit")}
            </MButton>
          </div>
        )}

        {/* OTP Input and Submit Button */}
        <div className={styles.otpInputSection}>
          <div className={styles.otpInputWrapper}>
            <MInputField
              placeholder={t("otp.placeholder")}
              variant="Outlined"
              size="xl"
              value={otpValue}
              inputMode="numeric"
              onChange={handleOtpChange}
              autoFocus
              type="text"
              showLabel={false}
              state={state}
              stateMessage={stateMessage}
              maxLength={6}
            />{" "}
          </div>
          <MButton
            size="xl"
            onClick={handleSubmit}
            disabled={otpValue.length !== 6 || submitting}
            variant="icon-only-filled"
            icon={<IconArrowNarrowRight />}
          />
        </div>

        {/* Resend Section */}
        {onResend && (
          <div className={styles.otpResendSection}>
            <div className="auth-tooltip-section">
              <p className="auth-otp-didntget">{t("otp.didntGetCode")}</p>
              <MButton
                variant="link"
                size="md"
                className="auth-otp-action-btn auth-otp-edit ml-auto"
                disabled={internalTimer > 0 || loading}
                onClick={() => {
                  if (internalTimer === 0 && !loading) handleResend();
                }}
              >
                {t("common.resend")}
              </MButton>
            </div>
            <span className="auth-otp-resend-label">
              Request new code in{" "}
              <span className="text-[var(--brand-colours-indigo-indigo-700)] font-bold">
                00:{internalTimer.toString().padStart(2, "0")}
              </span>
            </span>
          </div>
        )}
      </div>
    </FooterDrawer>
  );
};

export default MOtp;
